import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getUsersAdminService, sendOrderSmsAdminService, getOrdersAdminService, autoSaveOrderAdminService, getOrderLogsAdminService } from "../../../services/adminServices";
import { toastErrorMessage, toastSuccessMessage } from "../../../utils/toastMessage/toastMessage";
import http from "../../../services/configServices/httpService";
import { base_url } from "../../../services/configServices/config";
import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";

const OrdersAdmin = () => {
  const emptyRow = {
    id: Date.now(),
    localKey: `row-${Date.now()}-${Math.random()}`, // NEW: Stable render key
    customer: { firstName: "", phoneNumber: "", address: "" },
    courier: { _id: null, firstName: "", phoneNumber: "" },
    smsSentCustomer: false,
    smsSentCourier: false,
    status: "PENDING",
    description: ""
  };

  const [gridData, setGridData] = useState([]);
  const [availableCouriers, setAvailableCouriers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState("تغییری ایجاد نشده");
  const [showCompleted, setShowCompleted] = useState(true);
  const [colWidths, setColWidths] = useState(() => {
    const savedWidths = localStorage.getItem("axon_order_col_widths");
    if (savedWidths) {
      try {
        return JSON.parse(savedWidths);
      } catch (e) {
        console.error("Failed to parse saved column widths");
      }
    }
    // Fallback defaults if nothing is saved yet
    return {
      customerFirstName: 110,
      customerPhoneNumber: 120,
      customerAddress: 200,
      description: 180,
      courierPhone: 120,
    };
  });
  // Save column widths to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("axon_order_col_widths", JSON.stringify(colWidths));
  }, [colWidths]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [activeLogOrder, setActiveLogOrder] = useState(null);
  const [orderLogs, setOrderLogs] = useState([]);
  const [isLogsLoading, setIsLogsLoading] = useState(false);

  const socket = useSelector((state) => state.socket);
  //prevent double save
  const pendingCreations = useRef({});

  useEffect(() => {
    const fetchCouriers = async () => {
      try {
        const res = await getUsersAdminService({ role: "COURIER", itemsCount: 500 });
        if (res.status === 200) setAvailableCouriers(res.data.result?.users || []);
      } catch (error) {
        console.error("Failed to fetch couriers");
      }
    };
    fetchCouriers();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        const res = await getOrdersAdminService(showCompleted, page, limit, sortBy, sortOrder);

        if (res.status === 200) {
          const fetchedOrders = res.data.result?.orders || [];
          setTotalPages(res.data.result?.totalPages || 1);

          const formattedOrders = fetchedOrders.map(order => ({
            id: order._id,
            localKey: order._id,
            customer: {
              firstName: order.customerFirstName || "",
              phoneNumber: order.customerPhoneNumber || "",
              address: order.customerAddress || ""
            },
            courier: {
              _id: order.courier?._id || null,
              firstName: order.courier?.firstName || "",
              phoneNumber: order.courier?.phoneNumber || ""
            },
            smsSentCustomer: order.smsSentCustomer || false,
            smsSentCourier: order.smsSentCourier || false,
            status: order.status || "PENDING",
            description: order.description || "" // <-- مپ کردن توضیحات
          }));

          setGridData([...formattedOrders, { ...emptyRow, id: Date.now(), localKey: `row-${Date.now()}-${Math.random()}` }]);
        }
      } catch (error) {
        toastErrorMessage("خطا در دریافت سفارشات");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCompleted, page, limit, sortBy, sortOrder]);

  useEffect(() => {
    if (!socket || !socket.on) return;

    const handleOrderUpdated = (updatedOrder) => {
      setGridData((prevGrid) => {
        const newData = [...prevGrid];
        
        // 1. Check if the order already exists in our grid by its real ID
        const existingRowIndex = newData.findIndex(row => String(row.id) === String(updatedOrder._id));

        // 2. Check if we have an "unsaved" row that matches the incoming data
        // This stops the socket from duplicating a row we just created locally
        const matchingUnsavedRowIndex = newData.findIndex(row => 
          String(row.id).length !== 24 && // It's a local temporary ID
          row.customer.phoneNumber === updatedOrder.customerPhoneNumber &&
          row.customer.firstName === updatedOrder.customerFirstName
        );

        const targetKey = existingRowIndex >= 0 
          ? newData[existingRowIndex].localKey 
          : (matchingUnsavedRowIndex >= 0 ? newData[matchingUnsavedRowIndex].localKey : updatedOrder._id);
        console.log('targetKEy', targetKey);

        const formattedOrder = {
          id: updatedOrder._id,
          localKey: targetKey,
          customer: { 
            firstName: updatedOrder.customerFirstName || "", 
            phoneNumber: updatedOrder.customerPhoneNumber || "", 
            address: updatedOrder.customerAddress || "" 
          },
          courier: { 
            _id: updatedOrder.courier?._id || null, 
            firstName: updatedOrder.courier?.firstName || "", 
            phoneNumber: updatedOrder.courier?.phoneNumber || "" 
          },
          smsSentCustomer: updatedOrder.smsSentCustomer || false,
          smsSentCourier: updatedOrder.smsSentCourier || false,
          status: updatedOrder.status || "PENDING",
          description: updatedOrder.description || ""
        };

        if (existingRowIndex >= 0) {
          // If the order exists (real ID), update it
          newData[existingRowIndex] = formattedOrder;
        } else if (matchingUnsavedRowIndex >= 0) {
          // If we found a local unsaved row that matches the socket data, just update its ID!
          newData[matchingUnsavedRowIndex] = formattedOrder;
        } else {
          // Only if it doesn't exist at all (another admin truly created it), insert it
          newData.splice(newData.length - 1, 0, formattedOrder);
        }
        
        return newData;
      });
    };

    const handleOrderDeleted = (deletedOrderId) => {
      setGridData((prevGrid) => prevGrid.filter(row => row.id !== deletedOrderId));
    };

    socket.on("admin-order-updated", handleOrderUpdated);
    socket.on("admin-order-deleted", handleOrderDeleted);

    return () => {
      socket.off("admin-order-updated", handleOrderUpdated);
      socket.off("admin-order-deleted", handleOrderDeleted);
    };
  }, [socket]);

  const autoSaveRow = async (index, currentRowData) => {
    // جلوگیری از ذخیره ردیف خالی
    if (!currentRowData.customer.firstName && !currentRowData.customer.phoneNumber && !currentRowData.customer.address && !currentRowData.courier._id && !currentRowData.description) {
      return;
    }

    let currentId = currentRowData.id;
    const isNewRow = String(currentId).length !== 24;
    let resolveCreation;

    // ----- سیستم نگاشت و قفل -----
    if (isNewRow) {
      if (pendingCreations.current[currentId]) {
        try {
          // اگر قبلا ساخته شده یا در حال ساخت است، منتظر بمان یا آیدی ذخیره شده را بگیر
          currentId = await pendingCreations.current[currentId]; 
        } catch (error) {
          return; 
        }
      } else {
        // برای اولین بار، یک Promise می‌سازیم
        pendingCreations.current[currentId] = new Promise((resolve) => {
          resolveCreation = resolve;
        });
      }
    }
    // -----------------------------

    setSyncStatus("در حال ذخیره...");
    const payload = {
      orderId: currentId,
      customerFirstName: currentRowData.customer.firstName,
      customerPhoneNumber: currentRowData.customer.phoneNumber,
      customerAddress: currentRowData.customer.address,
      courierId: currentRowData.courier._id,
      status: currentRowData.status,
      description: currentRowData.description
    };

    try {
      const { status, data } = await autoSaveOrderAdminService(payload);
      
      if (status === 200 && data.result?.order?._id) {
        const newRealId = data.result.order._id;

        // تغییر طلایی: قفل را باز کن، اما آیدی واقعی را به جای آن ذخیره کن (پاک نکن!)
        if (resolveCreation) {
          resolveCreation(newRealId);
          pendingCreations.current[currentRowData.id] = newRealId; 
        }

        setGridData(prev => {
          const newData = [...prev];
          // اطمینان از آپدیت شدن ردیف درست
          if (newData[index] && (newData[index].id === currentRowData.id || newData[index].id === newRealId)) {
            newData[index].id = newRealId;
          }
          return newData;
        });
        setSyncStatus("ذخیره شد ✓");
      }
    } catch (error) {
      if (resolveCreation) delete pendingCreations.current[currentRowData.id];
      setSyncStatus("خطا در ذخیره ❌");
      toastErrorMessage("مشکلی در همگام‌سازی این ردیف به وجود آمد");
    }
  };

  const standardizeIranianPhone = (phone) => {
    if (!phone) return "";

    let p = String(phone);

    // 1. Convert Persian/Arabic numbers to English numbers
    p = p.replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
      .replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

    // 2. Remove all non-numeric characters (spaces, +, -, etc.)
    p = p.replace(/\D/g, "");

    // 3. Strip country codes (0098 or 98)
    if (p.startsWith("0098")) p = p.substring(4);
    else if (p.startsWith("98") && p.length === 12) p = p.substring(2);

    // 4. Ensure it starts with a 0 (if they just typed 914...)
    if (p.length === 10 && p.startsWith("9")) p = "0" + p;

    return p;
  };

  const handleCellChange = (index, field, value) => {
    setSyncStatus("در حال ویرایش...");
    const updatedGrid = [...gridData];
    const keys = field.split(".");

    if (keys.length === 2) {
      if (!updatedGrid[index][keys[0]]) updatedGrid[index][keys[0]] = {};
      updatedGrid[index][keys[0]][keys[1]] = value;
    } else {
      updatedGrid[index][field] = value;
    }

    if (field === "customer.phoneNumber") updatedGrid[index].smsSentCustomer = false;
    setGridData(updatedGrid);
    if (field === "status") autoSaveRow(index, updatedGrid[index]);
  };

  const handleBlur = (index, row) => {
    if (row.customer?.phoneNumber) {
      const formattedPhone = standardizeIranianPhone(row.customer.phoneNumber);

      // Update UI instantly if the format changed
      if (formattedPhone !== row.customer.phoneNumber) {
        setGridData(prev => {
          const newData = [...prev];
          newData[index].customer.phoneNumber = formattedPhone;
          return newData;
        });
        row.customer.phoneNumber = formattedPhone; // Update the reference for autoSaveRow
      }
    }

    autoSaveRow(index, row);
  };

  const handleCourierChange = (index, courierId) => {
    const updatedGrid = [...gridData];
    let newCourier = { _id: null, firstName: "", phoneNumber: "" };

    if (courierId) {
      const selectedCourier = availableCouriers.find(c => c._id === courierId);
      newCourier = { _id: selectedCourier._id, firstName: selectedCourier.firstName, phoneNumber: selectedCourier.phoneNumber };
    }

    updatedGrid[index].courier = newCourier;
    updatedGrid[index].smsSentCourier = false;
    setGridData(updatedGrid);
    autoSaveRow(index, updatedGrid[index]);
  };

  const handleAddRow = () => setGridData([...gridData, { ...emptyRow, id: Date.now(), localKey: `row-${Date.now()}-${Math.random()}` }]);

  const handleDeleteRow = async (index, rowId) => {
    if (!window.confirm("آیا از حذف این سفارش مطمئن هستید؟")) return;

    if (String(rowId).length === 24) {
      try {
        setSyncStatus("در حال حذف...");
        const res = await http.delete(`${base_url}/admin/order/${rowId}`);
        if (res.status === 200) toastSuccessMessage("سفارش با موفقیت حذف شد");
      } catch (error) {
        toastErrorMessage("خطا در حذف سفارش");
        setSyncStatus("خطا در حذف ❌");
        return;
      }
    }

    const updatedGrid = gridData.filter((_, i) => i !== index);
    setGridData(updatedGrid);
    setSyncStatus("بروزرسانی شد ✓");
  };

  const processSmsAction = async (index, orderRow, target) => {
    setSyncStatus("در حال ارسال...");
    const payload = {
      orderId: orderRow.id,
      customerFirstName: orderRow.customer.firstName,
      customerPhoneNumber: orderRow.customer.phoneNumber,
      customerAddress: orderRow.customer.address,
      courierId: orderRow.courier._id,
      target: target
    };

    try {
      const { status, data } = await sendOrderSmsAdminService(payload);
      if (status === 200) {
        toastSuccessMessage("پیامک ارسال و ثبت شد");
        setSyncStatus("ذخیره شد ✓");
        const updatedGrid = [...gridData];
        updatedGrid[index].id = data.result.order._id;
        if (target === "CUSTOMER") updatedGrid[index].smsSentCustomer = true;
        if (target === "COURIER") updatedGrid[index].smsSentCourier = true;
        setGridData(updatedGrid);
      }
    } catch ({ response }) {
      setSyncStatus("خطا در ذخیره ❌");
      if (response && response.status) toastErrorMessage(response.data.resultMessage || "خطا در ارسال پیامک");
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const handleViewLogs = async (row) => {
    if (String(row.id).length !== 24) return;
    setActiveLogOrder(row);
    setIsLogsLoading(true);
    try {
      const res = await getOrderLogsAdminService(row.id);
      if (res.status === 200) {
        setOrderLogs(res.data.result?.logs || []);
      }
    } catch (error) {
      toastErrorMessage("خطا در دریافت تاریخچه تغییرات");
    } finally {
      setIsLogsLoading(false);
    }
  };

  // تابع تغییر سایز با ماوس
  const handleResizeStart = (e, column) => {
    e.preventDefault();
    e.stopPropagation(); // جلوگیری از تداخل کلیک با رویداد مرتب‌سازی (Sorting)

    const startX = e.clientX;
    const startWidth = colWidths[column];

    const handleMouseMove = (moveEvent) => {
      // چون پنل راست‌چین است، کشیدن به سمت چپ باعث افزایش عرض می‌شود
      const newWidth = startWidth + (startX - moveEvent.clientX);
      setColWidths(prev => ({ ...prev, [column]: Math.max(newWidth, 60) })); // حداقل عرض 60 پیکسل
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = 'default';
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = 'col-resize';
  };

  if (isLoading) return <div className="text-center p-4">در حال بارگذاری...</div>;

  return (
    <div className="card border-0 shadow-sm mt-4">
      <SiteTitle title="سفارشات" />
      <div className="card-header bg-white border-bottom-0 py-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <h6 className="m-0 mb-0" style={{ fontSize: '15px', color: '#333', fontWeight: 'bold' }}>سفارشات</h6>
          <div className="form-check m-0 ms-3 d-flex align-items-center gap-2">
            <input
              className="form-check-input mt-0"
              type="checkbox"
              id="activeOrdersCheck"
              checked={showCompleted}
              onChange={(e) => {
                setShowCompleted(e.target.checked);
                setPage(1);
              }}
              style={{ cursor: "pointer" }}
            />
            <label className="form-check-label text-muted" htmlFor="activeOrdersCheck" style={{ fontSize: "13px", cursor: "pointer" }}>
              سفارشات فعال
            </label>
          </div>
        </div>
        <small className={`fw-medium ${syncStatus.includes('خطا') ? 'text-danger' : 'text-primary'}`}>{syncStatus}</small>
      </div>

      <div className="card-body p-0">
        <div className="table-responsive">
          {/* اضافه شدن minWidth به تیبل برای ایجاد اسکرول افقی */}
          <table className="table table-bordered table-hover table-striped align-middle mb-0" style={{ minWidth: "1250px" }}>
            <thead className="bg-light">
              <tr>
                {/* تغییر عرض‌ها از درصد به پیکسل برای جلوگیری از فشرده شدن */}
                <th
                  className="position-relative"
                  style={{ textAlign: "right", minWidth: `${colWidths.customerFirstName}px`, cursor: "pointer" }}
                  onClick={() => handleSort("customerFirstName")}
                >
                  نام مشتری {sortBy === "customerFirstName" && (sortOrder === "asc" ? "↑" : "↓")}

                  {/* دستگیره نامرئی برای تغییر سایز */}
                  <div
                    onMouseDown={(e) => handleResizeStart(e, 'customerFirstName')}
                    style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "8px", cursor: "col-resize", zIndex: 10 }}
                    className="bg-hover-secondary" // اختیاری: برای اینکه وقتی ماوس میره روش کمی تیره بشه
                  />
                </th>
                <th
                  className="position-relative"
                  style={{ textAlign: "right", minWidth: `${colWidths.customerPhoneNumber}px`, cursor: "pointer" }}
                  onClick={() => handleSort("customerPhoneNumber")}
                >
                  موبایل مشتری {sortBy === "customerPhoneNumber" && (sortOrder === "asc" ? "↑" : "↓")}

                  {/* دستگیره نامرئی برای تغییر سایز */}
                  <div
                    onMouseDown={(e) => handleResizeStart(e, 'customerPhoneNumber')}
                    style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "8px", cursor: "col-resize", zIndex: 10 }}
                  />
                </th>
                <th
                  className="position-relative"
                  style={{ textAlign: "right", minWidth: `${colWidths.customerAddress}px` }}
                >
                  آدرس مشتری
                  <div
                    onMouseDown={(e) => handleResizeStart(e, 'customerAddress')}
                    style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "8px", cursor: "col-resize", zIndex: 10 }}
                  />
                </th>
                <th
                  className="position-relative"
                  style={{ textAlign: "right", minWidth: `${colWidths.description}px` }}
                >
                  توضیحات
                  <div
                    onMouseDown={(e) => handleResizeStart(e, 'description')}
                    style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "8px", cursor: "col-resize", zIndex: 10 }}
                  />
                </th>
                <th style={{ textAlign: "right", minWidth: "140px", cursor: "pointer" }} onClick={() => handleSort("courier")}>
                  نام پیک {sortBy === "courier" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="position-relative"
                  style={{ textAlign: "right", minWidth: `${colWidths.courierPhone}px` }}
                >
                  موبایل پیک
                  <div
                    onMouseDown={(e) => handleResizeStart(e, 'courierPhone')}
                    style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "8px", cursor: "col-resize", zIndex: 10 }}
                  />
                </th>
                <th className="text-center" style={{ minWidth: "150px" }}>عملیات</th>
                <th style={{ textAlign: "right", minWidth: "110px", cursor: "pointer" }} onClick={() => handleSort("status")}>
                  وضعیت {sortBy === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th className="text-center" style={{ minWidth: "90px" }}>مدیریت</th>
              </tr>
            </thead>
            <tbody>
              {gridData.map((row, index) => (
                <tr key={row.localKey} className={row.status === "DONE" ? "bg-success-subtle" : row.status === "CANCELLED" ? "bg-danger-subtle" : ""}>
                  <td className="p-0">
                    <input
                      type="text"
                      className="form-control border-0 bg-transparent py-2"
                      style={{ fontSize: "13px" }}
                      value={row.customer?.firstName || ""}
                      onChange={(e) => handleCellChange(index, "customer.firstName", e.target.value)}
                      onBlur={() => handleBlur(index, row)}
                    />
                  </td>
                  <td className="p-0">
                    <input
                      type="text"
                      className="form-control border-0 bg-transparent py-2"
                      style={{ fontSize: "13px", direction: "ltr", textAlign: "right" }}
                      value={row.customer?.phoneNumber || ""}
                      onChange={(e) => handleCellChange(index, "customer.phoneNumber", e.target.value)}
                      onBlur={() => handleBlur(index, row)}
                    />
                  </td>
                  <td className="p-0">
                    <input
                      type="text"
                      className="form-control border-0 bg-transparent py-2"
                      style={{ fontSize: "13px" }}
                      value={row.customer?.address || ""}
                      onChange={(e) => handleCellChange(index, "customer.address", e.target.value)}
                      onBlur={() => handleBlur(index, row)}
                    />
                  </td>
                  {/* سلول ورودی توضیحات */}
                  <td className="p-0">
                    <input
                      type="text"
                      className="form-control border-0 bg-transparent py-2"
                      style={{ fontSize: "13px" }}
                      value={row.description || ""}
                      onChange={(e) => handleCellChange(index, "description", e.target.value)}
                      onBlur={() => handleBlur(index, row)}
                      placeholder="..."
                    />
                  </td>
                  <td className="p-0">
                    <select
                      className="form-select border-0 bg-transparent py-2 shadow-none"
                      style={{ fontSize: "13px", paddingRight: "8px", paddingLeft: "30px", backgroundPosition: "left 0.5rem center" }}
                      value={row.courier?._id || ""}
                      onChange={(e) => handleCourierChange(index, e.target.value)}
                    >
                      <option value="">انتخاب پیک...</option>
                      {availableCouriers.map((courier) => (
                        <option key={courier._id} value={courier._id}>
                          {courier.firstName} {courier.lastName}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-0">
                    <input
                      type="text"
                      className="form-control border-0 bg-transparent py-2 text-muted"
                      style={{ fontSize: "13px", direction: "ltr", textAlign: "right" }}
                      value={row.courier?.phoneNumber || ""}
                      disabled
                    />
                  </td>
                  <td className="p-2 text-center align-middle">
                    <div className="d-flex gap-1 justify-content-center">
                      <button
                        onClick={() => { if (!row.smsSentCustomer) processSmsAction(index, row, "CUSTOMER"); }}
                        className={`btn btn-sm w-50 px-0 ${row.smsSentCustomer ? 'btn-success' : 'btn-outline-primary'}`}
                        style={{ fontSize: "11px" }}
                        disabled={row.smsSentCustomer}
                      >
                        {row.smsSentCustomer ? '✓ مشتری' : 'مشتری'}
                      </button>
                      <button
                        onClick={() => { if (!row.smsSentCourier) processSmsAction(index, row, "COURIER"); }}
                        className={`btn btn-sm w-50 px-0 ${row.smsSentCourier ? 'btn-success' : 'btn-outline-secondary'}`}
                        style={{ fontSize: "11px" }}
                        disabled={row.smsSentCourier}
                      >
                        {row.smsSentCourier ? '✓ پیک' : 'پیک'}
                      </button>
                    </div>
                  </td>
                  <td className="p-0">
                    <select
                      className={`form-select border-0 bg-transparent py-2 shadow-none fw-semibold ${row.status === "DONE" ? "text-success" : row.status === "CANCELLED" ? "text-danger" : "text-warning"}`}
                      style={{ fontSize: "13px", paddingRight: "8px", paddingLeft: "30px", backgroundPosition: "left 0.5rem center" }}
                      value={row.status}
                      onChange={(e) => handleCellChange(index, "status", e.target.value)}
                    >
                      <option value="PENDING">در حال انجام</option>
                      <option value="DONE">انجام شد</option>
                      <option value="CANCELLED">لغو شد</option>
                    </select>
                  </td>
                  <td className="p-2 text-center align-middle">
                    <div className="d-flex justify-content-center gap-2">
                      {String(row.id).length === 24 && (
                        <button onClick={() => handleViewLogs(row)} className="btn btn-sm btn-light text-muted p-1" data-bs-toggle="modal" data-bs-target="#orderLogsModal">
                          <Icon icon="solar:history-line-duotone" className="text-lg" />
                        </button>
                      )}
                      <button onClick={() => handleDeleteRow(index, row.id)} className="btn btn-sm btn-light text-danger p-1">
                        <Icon icon="solar:trash-bin-trash-outline" className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3">
        <div className="d-flex align-items-center gap-2">
          <select
            className="form-select form-select-sm shadow-none"
            style={{ width: "80px", fontSize: "13px" }}
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <span className="text-muted" style={{ fontSize: "13px" }}>آیتم در هر صفحه</span>
        </div>

        <div className="d-flex align-items-center gap-3">
          <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>قبلی</button>
          <span style={{ fontSize: "13px" }}>صفحه {page} از {totalPages}</span>
          <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages || totalPages === 0} onClick={() => setPage(page + 1)}>بعدی</button>
        </div>

        <button className="btn btn-sm btn-light border fw-semibold" onClick={handleAddRow}>+ افزودن ردیف جدید</button>
      </div>

      <div className="modal fade" id="orderLogsModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content radius-12">
            <div className="modal-header border-bottom-0 py-3">
              <h6 className="modal-title fw-bold" style={{ fontSize: "15px" }}>
                تاریخچه تغییرات سفارش {activeLogOrder?.customer?.firstName}
              </h6>
              <button type="button" className="btn-close shadow-none" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body p-3" style={{ maxHeight: "400px", overflowY: "auto" }}>
              {isLogsLoading ? (
                <div className="text-center py-3 text-muted fs-7">در حال بارگذاری تاریخچه...</div>
              ) : orderLogs.length === 0 ? (
                <div className="text-center py-3 text-muted fs-7">هیچ تغییراتی برای این سفارش ثبت نشده است.</div>
              ) : (
                <div className="timeline-container px-2" style={{ borderRight: "2px solid #eee" }}>
                  {orderLogs.map((log) => (
                    <div key={log._id} className="mb-3 position-relative pe-3" style={{ direction: "rtl" }}>
                      <div className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>{log.description}</div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        توسط: {log.admin?.firstName} {log.admin?.lastName} | {new Date(log.createdAt).toLocaleTimeString("fa-IR")} - {new Date(log.createdAt).toLocaleDateString("fa-IR")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default OrdersAdmin;